import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone } from "lucide-react";
import { CONTACT } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Customized Tour Package (Domestic / Intl)",
  "Flight / Bus / Railway Tickets",
  "Visa & Passport Assistance",
  "Corporate Tour / Office Trip",
  "Group / Friends Getaway",
];

const inputCls =
  "w-full px-3.5 py-2.5 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-700 bg-white text-stone-900";
const labelCls = "block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], details: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Enquiry received! Our travel desk will call you back shortly.");
      setForm({ name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], details: "" });
    } catch (err) {
      toast.error("Could not submit right now. Please reach us on WhatsApp or call instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-stone-50" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-stone-900 text-white rounded-3xl p-8 sm:p-12 lg:p-16 shadow-2xl relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-amber-600/20 blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold tracking-widest text-amber-400 uppercase">Quick Trip Enquiry</span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight font-serif-display leading-tight">
                Let's plan your upcoming travel
              </h2>
              <p className="text-stone-300 text-sm leading-relaxed">
                Fill this simple form or connect instantly via WhatsApp or phone. We will respond with exact quotes and itinerary options.
              </p>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider">Instant Connect Rails</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={CONTACT.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" aria-hidden="true" />
                    <span>WhatsApp Enquiry Desk</span>
                  </a>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="contact-call-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-800 hover:bg-stone-700 border border-stone-700 text-white text-xs font-semibold transition-colors"
                  >
                    <Phone className="w-4 h-4" aria-hidden="true" />
                    <span>Call Parth Travels · {CONTACT.phoneDisplay}</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 bg-white text-stone-900 rounded-2xl p-6 sm:p-8 shadow-lg">
              <form className="space-y-4" onSubmit={onSubmit} data-testid="enquiry-form">
                <div>
                  <label htmlFor="enquiry-name" className={labelCls}>Your Full Name</label>
                  <input id="enquiry-name" data-testid="enquiry-name-input" type="text" className={inputCls} placeholder="e.g. Rajesh Sharma" required value={form.name} onChange={update("name")} />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className={labelCls}>Phone / WhatsApp Number</label>
                  <input id="enquiry-phone" data-testid="enquiry-phone-input" type="tel" className={inputCls} placeholder="e.g. +91 98765 43210" required value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className={labelCls}>Email (optional — for instant confirmation)</label>
                  <input id="enquiry-email" data-testid="enquiry-email-input" type="email" className={inputCls} placeholder="e.g. you@gmail.com" value={form.email} onChange={update("email")} />
                </div>
                <div>
                  <label htmlFor="enquiry-service" className={labelCls}>Service Needed</label>
                  <select id="enquiry-service" data-testid="enquiry-service-select" className={inputCls} value={form.service} onChange={update("service")}>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="enquiry-details" className={labelCls}>Destination &amp; Approximate Dates</label>
                  <input id="enquiry-details" data-testid="enquiry-details-input" type="text" className={inputCls} placeholder="e.g. Manali in May, 4 passengers" value={form.details} onChange={update("details")} />
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    data-testid="enquiry-submit-btn"
                    disabled={submitting}
                    className="w-full py-3.5 px-4 bg-amber-700 hover:bg-amber-800 disabled:opacity-60 text-white font-bold text-sm rounded-xl shadow-md transition-colors"
                  >
                    {submitting ? "Sending..." : "Submit Travel Enquiry"}
                  </button>
                  <p className="text-[11px] text-stone-700 text-center mt-2">We respect your privacy and never share your contact details.</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
